import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { detectLanguageFromGeoIP } from '../i18n';

interface LanguageContextType {
    currentLanguage: string;
    changeLanguage: (language: string) => void;
    isLoading: boolean;
    detectedFromGeoIP: boolean;
    detectedCountry?: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

interface LanguageProviderProps {
    children: ReactNode;
}

// Normalize language codes to our supported languages
const normalizeLanguageCode = (languageCode: string): string => {
    const code = languageCode.toLowerCase();

    // Map regional variants to our supported languages
    if (code.startsWith('en')) return 'en'; // en-US, en-GB, en-CA, etc. -> en
    if (code.startsWith('es')) return 'es'; // es-ES, es-MX, es-AR, etc. -> es
    if (code.startsWith('fr')) return 'fr'; // fr-FR, fr-CA, etc. -> fr
    if (code.startsWith('de')) return 'de'; // de-DE, de-AT, de-CH, etc. -> de
    if (code.startsWith('tr')) return 'tr'; // tr-TR -> tr
    if (code.startsWith('nl')) return 'nl'; // nl-NL, nl-BE -> nl

    return 'en'; // Default fallback
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const { i18n, t } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState('en'); // Default to English
    const [isLoading] = useState(false); // Changed to false initially
    const [detectedFromGeoIP, setDetectedFromGeoIP] = useState(false);
    const [detectedCountry, setDetectedCountry] = useState<string>();

    // Update document title when language changes
    useEffect(() => {
        if (i18n.isInitialized) {
            document.title = t('app.pageTitle');
        }
    }, [currentLanguage, i18n.isInitialized, t]);

    // Update HTML lang attribute when language changes
    useEffect(() => {
        if (currentLanguage) {
            document.documentElement.lang = currentLanguage;

            // Add meta tag to prevent automatic translation
            let metaTag = document.querySelector('meta[name="google"]');
            if (!metaTag) {
                metaTag = document.createElement('meta');
                metaTag.setAttribute('name', 'google');
                document.head.appendChild(metaTag);
            }
            metaTag.setAttribute('content', 'notranslate');

            // Set document direction (left-to-right for all our languages)
            document.documentElement.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Sync currentLanguage with i18n.language when it changes
    useEffect(() => {
        if (i18n.language && i18n.language !== currentLanguage) {
            const normalizedLanguage = normalizeLanguageCode(i18n.language);
            setCurrentLanguage(normalizedLanguage);
        }
    }, [i18n.language, currentLanguage]);

    useEffect(() => {
        const initializeLanguage = async () => {
            try {
                // Check if user has a saved language preference
                const savedLanguage = localStorage.getItem('i18nextLng');

                if (savedLanguage) {
                    const normalizedLanguage = normalizeLanguageCode(savedLanguage);
                    setCurrentLanguage(normalizedLanguage);
                    await i18n.changeLanguage(normalizedLanguage);
                } else {
                    // Check if we have a cached GeoIP result
                    const cachedGeoIP = localStorage.getItem('geoip_language');
                    const cachedCountry = localStorage.getItem('geoip_country');

                    if (cachedGeoIP) {
                        setCurrentLanguage(cachedGeoIP);
                        await i18n.changeLanguage(cachedGeoIP);
                        setDetectedFromGeoIP(true);
                        if (cachedCountry) {
                            setDetectedCountry(cachedCountry);
                        }
                    } else {
                        // Start with browser language as default, then try GeoIP in background
                        const browserLanguage = navigator.language || navigator.languages?.[0];
                        let initialLanguage = 'en';
                        if (browserLanguage) {
                            const normalizedBrowserLang = normalizeLanguageCode(browserLanguage);
                            if (normalizedBrowserLang !== 'en') {
                                initialLanguage = normalizedBrowserLang;
                            }
                        }

                        setCurrentLanguage(initialLanguage);
                        await i18n.changeLanguage(initialLanguage);

                        // Try GeoIP detection in background (non-blocking)
                        detectLanguageFromGeoIPInBackground();
                    }
                }
            } catch (error) {
                // Fallback to browser language or English
                const browserLanguage = navigator.language || navigator.languages?.[0];
                const fallbackLanguage = browserLanguage ? normalizeLanguageCode(browserLanguage) : 'en';
                setCurrentLanguage(fallbackLanguage);
                await i18n.changeLanguage(fallbackLanguage);
            }
        };

        const detectLanguageFromGeoIPInBackground = async () => {
            try {
                const detectedLanguage = await detectLanguageFromGeoIP();

                // Only update if GeoIP detected a different language and user hasn't manually changed
                if (detectedLanguage !== 'en' && detectedLanguage !== currentLanguage) {
                    setCurrentLanguage(detectedLanguage);
                    await i18n.changeLanguage(detectedLanguage);

                    // Cache the GeoIP result
                    localStorage.setItem('geoip_language', detectedLanguage);
                    setDetectedFromGeoIP(true);

                    // Try to get the country name for display
                    try {
                        const response = await fetch('https://ipapi.co/json/');
                        const data = await response.json();
                        const countryName = data.country_name;
                        setDetectedCountry(countryName);
                        localStorage.setItem('geoip_country', countryName);
                    } catch (error) {
                        console.warn('Could not fetch country name:', error);
                    }
                }
            } catch (error) {
                console.warn('Background GeoIP detection failed:', error);
            }
        };

        initializeLanguage();
    }, [i18n]); // Remove currentLanguage from dependency array

    const changeLanguage = async (language: string) => {
        try {
            await i18n.changeLanguage(language);
            setCurrentLanguage(language);
            localStorage.setItem('i18nextLng', language);
            // Clear GeoIP detection status and cache when user manually changes language
            setDetectedFromGeoIP(false);
            setDetectedCountry(undefined);
            localStorage.removeItem('geoip_language');
            localStorage.removeItem('geoip_country');
        } catch (error) {
            console.error('Failed to change language:', error);
        }
    };

    const value: LanguageContextType = {
        currentLanguage,
        changeLanguage,
        isLoading,
        detectedFromGeoIP,
        detectedCountry,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
